import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, Receipt } from "@phosphor-icons/react";

const statusColors = {
  initiated: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  pendiente: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  complete: "bg-green-500/20 text-green-400 border-green-500/30",
  paid: "bg-green-500/20 text-green-400 border-green-500/30",
};

export default function PaymentHistory() {
  const { t } = useLang();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/history").then(({ data }) => setTransactions(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payment-history-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">{t("nav.pagos")}</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">{t("payments.titulo")}</h1>
      </div>

      {transactions.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <Receipt size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">{t("payments.no_pagos")}</p>
        </div>
      ) : (
        <div className="card-tactical overflow-hidden" data-testid="transactions-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">{t("payments.fecha")}</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">{t("payments.detalle")}</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">{t("payments.monto")}</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">{t("payments.estado")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx, i) => (
                <TableRow key={tx.id || i} className="border-white/5 hover:bg-white/5" data-testid={`tx-row-${i}`}>
                  <TableCell className="text-gray-400 text-sm">{tx.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CreditCard size={16} className="text-[#CCFF00]" />
                      <div>
                        <p className="text-sm text-white font-semibold">
                          {tx.rental_info ? `${tx.rental_info.court_name} - ${tx.rental_info.date}` : `Pago #${tx.session_id?.slice(0, 8)}`}
                        </p>
                        {tx.rental_info && <p className="text-xs text-gray-500">{tx.rental_info.time_slot}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-[#CCFF00] font-bold">
                    ${tx.amount} {tx.currency?.toUpperCase()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={`badge-status ${statusColors[tx.payment_status] || statusColors[tx.status] || ""}`}>
                      {tx.payment_status === "paid" || tx.payment_status === "pagada" ? t("payments.pagado") : tx.payment_status === "pendiente" ? t("payments.pendiente") : tx.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
